import type { StaticAssetPath } from '../types/asset-paths'

/**
 * Retrieves the public URL for a static asset from the PiBo S3 bucket.
 * Automatically selects the correct bucket (production or staging) based on the environment.
 *
 * @param assetPath - The path to the static asset (must be a valid StaticAssetPath)
 * @returns The fully qualified HTTPS URL to access the static asset
 *
 * @remarks
 * - Environment is determined by `process.env.NEXT_PUBLIC_PROD_ENV`
 * - Uses staging bucket for 'staging' and 'development' environments
 * - Uses production bucket for all other environments
 * - Asset paths are type-checked against available static assets
 *
 * @example
 * // Get logo URL (production)
 * const logoUrl = getStaticAssetUrl('logo/logo.png')
 * // Returns: 'https://{public-url}/logo/logo.png'
 *
 * @example
 * // Get avatar URL (staging)
 * const avatarUrl = getStaticAssetUrl('avatar/1.png')
 * // Returns: 'https://{public-url}/avatar/1.png'
 *
 * @example
 * // Using with STATIC_ASSETS constant for better autocomplete
 * import { STATIC_ASSETS } from '../types/asset-paths'
 * const illustrationUrl = getStaticAssetUrl(STATIC_ASSETS.ILLUSTRATION_KOHI_PNG)
 */
export function getStaticAssetUrl(assetPath: StaticAssetPath | (string & {})): string {
  // Vite client: process.env is undefined; use import.meta.env. Node/Next.js: use process.env.
  const baseUrl =
    (typeof import.meta !== 'undefined' &&
      (import.meta as { env?: Record<string, string> }).env?.NEXT_PUBLIC_R2_PUBLIC_URL) ??
    process.env.NEXT_PUBLIC_R2_PUBLIC_URL
  return `${baseUrl}/${assetPath}`
}
